import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  UploadCloud,
  FileText,
  Loader2,
  Wand2,
  Trash2,
  BookOpen,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABEL = {
  deck: "Pitch deck",
  paper: "Scientific paper",
  grant: "Grant application",
  other: "Document",
};

/**
 * KnowledgeRepositorySection
 * Multi-file upload for building the company knowledge base.
 * When user clicks "Auto-fill profile from documents", it calls
 * /api/knowledge/extract-profile and passes the merged fields to onExtracted(fields).
 */
export default function KnowledgeRepositorySection({ onExtracted }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [dragging, setDragging] = useState(false);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/knowledge/documents`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setDocs(data.items || []);
      }
    } catch {}
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const uploadFiles = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(fileList).forEach((f) => fd.append("files", f));
      const res = await fetch(`${BACKEND_URL}/api/knowledge/documents/upload`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!res.ok) throw new Error("upload failed");
      const data = await res.json();
      toast.success(`${data.count} document${data.count === 1 ? "" : "s"} added`);
      fetchDocs();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer?.files) uploadFiles(e.dataTransfer.files);
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/knowledge/documents/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        setDocs((d) => d.filter((x) => x.id !== id));
      }
    } catch {}
  };

  const handleExtract = async () => {
    if (!docs.length) {
      toast.error("Upload at least one document first");
      return;
    }
    setExtracting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/knowledge/extract-profile`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("extract failed");
      const data = await res.json();
      const extracted = data.extracted || {};
      const fieldsCount = Object.keys(extracted).length;
      if (fieldsCount === 0) {
        toast.info("AI could not confidently extract fields from your documents");
        return;
      }
      onExtracted && onExtracted(extracted);
      toast.success(`AI filled ${fieldsCount} field${fieldsCount === 1 ? "" : "s"} — please review below`);
    } catch {
      toast.error("Extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  return (
    <Card className="border-2 border-teal-200 bg-gradient-to-br from-teal-50/60 to-white" data-testid="knowledge-repository">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-teal-500/15 flex items-center justify-center shrink-0">
              <BookOpen className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Knowledge repository</h3>
              <p className="text-sm text-slate-600 mt-0.5">
                Upload your deck, papers, grant applications and any documents about your company. Operon&apos;s AI reads them across the whole platform to spot strengths, gaps and opportunities.
              </p>
            </div>
          </div>
        </div>

        {/* Drag & drop / click area */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          className={`rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
            dragging ? "border-teal-500 bg-teal-50" : "border-slate-300 bg-white"
          }`}
          data-testid="knowledge-dropzone"
        >
          <input
            type="file"
            id="knowledge-upload"
            className="hidden"
            multiple
            accept=".pdf,.pptx,.ppt,.txt,.md,.docx"
            onChange={(e) => uploadFiles(e.target.files)}
          />
          <UploadCloud className="w-9 h-9 text-slate-400 mx-auto mb-2" />
          <div className="text-sm text-slate-700 font-medium mb-1">Drop files here or</div>
          <Button
            variant="outline"
            onClick={() => document.getElementById("knowledge-upload").click()}
            disabled={uploading}
            data-testid="btn-knowledge-choose"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UploadCloud className="w-4 h-4 mr-2" />}
            {uploading ? "Uploading…" : "Choose files"}
          </Button>
          <div className="text-xs text-slate-500 mt-3">
            PDF, PPTX, DOCX, TXT · Multiple files allowed
          </div>
        </div>

        {/* Uploaded documents list */}
        {docs.length > 0 && (
          <div className="space-y-2" data-testid="knowledge-list">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">
              In your repository ({docs.length})
            </div>
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3">
                <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{d.filename}</div>
                  <div className="text-xs text-slate-500">
                    {TYPE_LABEL[d.doc_type] || "Document"} · {(d.size_bytes / 1024).toFixed(0)} KB
                    {d.has_text ? "" : " · No text extracted"}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(d.id)}
                  className="text-slate-400 hover:text-rose-600 p-1"
                  aria-label="Delete document"
                  data-testid={`delete-doc-${d.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Auto-fill action */}
        <div className="pt-2 flex items-center justify-between gap-3 border-t border-slate-100">
          <div className="text-xs text-slate-600 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-teal-500" />
            Operon will read every document to auto-fill your profile below.
          </div>
          <Button
            onClick={handleExtract}
            disabled={extracting || docs.length === 0}
            className="bg-teal-500 hover:bg-teal-600 text-white"
            data-testid="btn-extract-knowledge"
          >
            {extracting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
            {extracting ? "Reading documents…" : "Auto-fill profile from documents"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
